import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
    Phone,
    Clock,
    Download,
    Loader2,
    Users,
    BarChart3,
    TrendingUp,
    RefreshCw,
} from "lucide-react";
import { formatMYT } from "@util/date";
import { cn } from "@util/tailwind";
import type { CallSession, SessionStatus, SentimentLevel } from "@type/agent";
import { getAgentCallSessions, updateCampaignStatus } from "@service/agent";
import {
    formatDuration,
    statusBadgeConfig,
    getScoreColor,
    getScoreLabel,
    sentimentChipConfig,
    chipVariantStyles,
    chipDotColor,
    isSessionEffectivelyTerminal,
} from "@util/agent-scoring";
import { Card, CardContent, CardHeader, CardTitle } from "@component/card";
import { Button } from "@component/button";

interface CampaignResultsProps {
    campaignId: string;
    sessionCount: number;
    contactCount: number;
    retryAttempts?: number;
    campaignStatus?: string;
    onCampaignCompleted?: () => void;
}

const statusColors: Record<SessionStatus, string> = {
    pending: "bg-gray-400",
    scheduled: "bg-indigo-500",
    active: "bg-blue-500",
    analyzing: "bg-amber-500",
    results_ready: "bg-green-500",
    failed: "bg-red-500",
};

export function CampaignResults({ campaignId, sessionCount, contactCount, retryAttempts, campaignStatus, onCampaignCompleted }: CampaignResultsProps) {
    const navigate = useNavigate();
    const [sessions, setSessions] = useState<CallSession[]>([]);
    const [loading, setLoading] = useState(true);
    const [sessionsLoaded, setSessionsLoaded] = useState(false);

    const isInitialLoad = useRef(true);
    const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const fetchSessions = async (silent = false) => {
        if (!campaignId) return;
        if (!silent) {
            setLoading(true);
            setSessionsLoaded(false);
        }
        try {
            const res = await getAgentCallSessions({ campaignId, limit: 1000 });
            setSessions(res.sessions);
            setSessionsLoaded(true);
        } catch {
            if (!silent) {
                setSessions([]);
                setSessionsLoaded(false);
            }
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        isInitialLoad.current = true;
        fetchSessions();
    }, [campaignId]);

    // Filter out test calls for stats
    const realSessions = sessions.filter((s) => !s.isTestCall);

    // Deduplicate by contactPhone — count unique contacts that have been called
    const uniqueContactsCalled = (() => {
        const seen = new Set<string>();
        for (const s of realSessions) {
            if (s.contactPhone) seen.add(s.contactPhone);
        }
        return seen.size;
    })();

    // Check if all real sessions are in terminal states (or stale zombies)
    const allSessionsTerminal = realSessions.length > 0 && realSessions.every(isSessionEffectivelyTerminal);

    // Poll for live sessions — exclude stale zombie sessions from polling
    const hasLiveSessions = sessions.some(
        (s) => ["pending", "scheduled", "active", "analyzing"].includes(s.status) && !isSessionEffectivelyTerminal(s)
    );

    useEffect(() => {
        if (!sessionsLoaded || !hasLiveSessions) {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
            return;
        }
        pollRef.current = setInterval(() => fetchSessions(true), 5000);
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
    }, [sessionsLoaded, hasLiveSessions, campaignId]);

    // Auto-complete campaign when all contacts have been called AND all sessions are terminal
    useEffect(() => {
        if (
            sessionsLoaded &&
            campaignStatus === "active" &&
            contactCount > 0 &&
            uniqueContactsCalled >= contactCount &&
            allSessionsTerminal
        ) {
            updateCampaignStatus(campaignId, "completed")
                .then(() => onCampaignCompleted?.())
                .catch(() => { /* silent — status update is best-effort */ });
        }
    }, [sessionsLoaded, uniqueContactsCalled, contactCount, campaignStatus, campaignId, onCampaignCompleted, allSessionsTerminal]);

    // Derived metrics (using real sessions only, excluding test calls)
    const completionRate = contactCount > 0 ? Math.min(100, Math.round((uniqueContactsCalled / contactCount) * 100)) : 0;

    const avgDuration = realSessions.length > 0
        ? Math.round(realSessions.reduce((sum, s) => sum + s.duration, 0) / realSessions.length)
        : 0;

    const scoredSessions = realSessions.filter((s) => s.score > 0);
    const avgScore = scoredSessions.length > 0
        ? Math.round(scoredSessions.reduce((sum, s) => sum + s.score, 0) / scoredSessions.length)
        : 0;

    // Status breakdown (real sessions only)
    const statusBreakdown = realSessions.reduce((acc, s) => {
        acc[s.status] = (acc[s.status] || 0) + 1;
        return acc;
    }, {} as Record<SessionStatus, number>);

    // Sentiment breakdown (real sessions only)
    const sentimentBreakdown = realSessions.reduce((acc, s) => {
        acc[s.sentiment] = (acc[s.sentiment] || 0) + 1;
        return acc;
    }, {} as Record<SentimentLevel, number>);

    const handleExportCSV = () => {
        const headers = ["No", "Call ID", "Phone Number", "Name", "Type", "Date & Time", "Messages", "Duration (s)", "Retries", "Score", "Sentiment", "Call Status"];
        const rows = sessions.map((s, i) => [
            i + 1,
            s.id.slice(0, 8),
            s.contactPhone || "",
            s.contactName || "",
            s.isTestCall ? "Test" : "Real",
            formatMYT(s.startedAt, "YYYY-MM-DD HH:mm:ss"),
            s.messageCount ?? 0,
            s.duration,
            s.retryCount ?? 0,
            s.score || "",
            s.sentiment || "",
            s.status,
        ]);
        const csv = [headers.join(","), ...rows.map((r) => r.map((v) => `"${v}"`).join(","))].join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `campaign-results-${campaignId.slice(0, 8)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Overview Stats — always visible from campaign data */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                <StatCard icon={Phone} label="Contacts Called" value={uniqueContactsCalled.toString()} />
                <StatCard icon={Users} label="Contacts Listed" value={contactCount.toString()} />
                <StatCard icon={TrendingUp} label="Completion Rate" value={`${completionRate}%`} />
                <StatCard icon={Clock} label="Avg Duration" value={sessionsLoaded && realSessions.length > 0 ? formatDuration(avgDuration) : "--"} />
                <StatCard
                    icon={BarChart3}
                    label="Avg Score"
                    value={scoredSessions.length > 0 ? `${avgScore}%` : "--"}
                    valueColor={scoredSessions.length > 0 ? getScoreColor(avgScore) : undefined}
                    subtitle={scoredSessions.length > 0 ? getScoreLabel(avgScore) : undefined}
                />
            </div>

            {/* Analytics Row — only when sessions are loaded */}
            {sessionsLoaded && realSessions.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Call Status Breakdown */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-sm">Call Status Breakdown</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {(Object.entries(statusBreakdown) as [SessionStatus, number][])
                                    .sort(([, a], [, b]) => b - a)
                                    .map(([status, count]) => {
                                        const config = statusBadgeConfig[status];
                                        const pct = Math.round((count / realSessions.length) * 100);
                                        return (
                                            <div key={status} className="space-y-1">
                                                <div className="flex items-center justify-between text-sm">
                                                    <span className="text-gray-600 dark:text-gray-400">{config?.label || status}</span>
                                                    <span className="text-gray-900 dark:text-white font-medium">{count} <span className="text-gray-400 font-normal">({pct}%)</span></span>
                                                </div>
                                                <div className="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                                                    <div
                                                        className={cn("h-full rounded-full transition-all", statusColors[status] || "bg-gray-400")}
                                                        style={{ width: `${pct}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        </CardContent>
                    </Card>

                    {/* Sentiment Distribution */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="text-sm">Sentiment Distribution</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {Object.keys(sentimentBreakdown).length > 0 ? (
                                <div className="space-y-3">
                                    {(Object.entries(sentimentBreakdown) as [SentimentLevel, number][])
                                        .sort(([, a], [, b]) => b - a)
                                        .map(([sentiment, count]) => {
                                            const pct = Math.round((count / realSessions.length) * 100);
                                            return (
                                                <div key={sentiment} className="flex items-center justify-between">
                                                    {(() => {
                                                        const sc = sentimentChipConfig[sentiment];
                                                        return (
                                                            <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border", chipVariantStyles[sc.variant])}>
                                                                <span className={cn("w-1.5 h-1.5 rounded-full", chipDotColor[sc.variant])} />
                                                                {sc.label}
                                                            </span>
                                                        );
                                                    })()}
                                                    <span className="text-sm text-gray-900 dark:text-white font-medium">{count} <span className="text-gray-400 font-normal">({pct}%)</span></span>
                                                </div>
                                            );
                                        })}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400 py-4 text-center">No sentiment data available.</p>
                            )}
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Retry button if sessions failed to load */}
            {!sessionsLoaded && !loading && (
                <div className="flex items-center justify-between gap-3 py-4 px-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                    <div>
                        <p className="text-sm font-medium text-amber-700 dark:text-amber-400">Could not load call results.</p>
                        <p className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">This may be a temporary connection issue. The overview stats above are still accurate.</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => fetchSessions()} className="flex-shrink-0">
                        <RefreshCw className="w-3.5 h-3.5 mr-1" />
                        Try Again
                    </Button>
                </div>
            )}

            {/* Results Table */}
            <Card>
                <CardHeader className="flex-row items-center justify-between">
                    <CardTitle>Call Results</CardTitle>
                    {sessions.length > 0 && (
                        <Button variant="outline" size="sm" onClick={handleExportCSV}>
                            <Download className="w-4 h-4 mr-1" />
                            Export CSV
                        </Button>
                    )}
                </CardHeader>
                <CardContent className="p-0">
                    {sessions.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[900px]">
                                <thead>
                                    <tr className="border-b border-gray-100">
                                        <th className="text-left pl-6 pr-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">No</th>
                                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Call ID</th>
                                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Phone Number</th>
                                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                                        <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</th>
                                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Messages</th>
                                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                                        <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Retries</th>
                                        <th className="text-right pr-6 pl-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sessions.map((session, index) => {
                                        const config = statusBadgeConfig[session.status] || { label: session.status, variant: "default" as const };
                                        return (
                                            <tr key={session.id} onClick={() => navigate(`/agent/calls/${session.id}`)} className="border-b border-gray-50 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer">
                                                <td className="pl-6 pr-4 py-3 text-sm text-gray-400">{index + 1}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 font-mono">{session.id.slice(0, 8)}</td>
                                                <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">
                                                    <span className="inline-flex items-center gap-1.5">
                                                        {session.contactPhone || "--"}
                                                        {session.isTestCall && (
                                                            <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] font-semibold rounded bg-indigo-50 text-indigo-600 border border-indigo-200 flex-shrink-0">Test</span>
                                                        )}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500">{session.contactName || "--"}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500">{formatMYT(session.startedAt, "D MMM YYYY, h:mm A")}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 text-right">{session.messageCount ?? 0}</td>
                                                <td className="px-4 py-3 text-sm text-gray-500 text-right">{formatDuration(session.duration)}</td>
                                                <td className="px-4 py-3 text-sm text-right">
                                                    {session.score > 0 ? (
                                                        <span className={cn("font-medium", getScoreColor(session.score))}>{session.score}</span>
                                                    ) : (
                                                        <span className="text-gray-400">--</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-500 text-right">
                                                    {session.isTestCall
                                                        ? <span className="text-gray-400">N/A</span>
                                                        : (session.maxRetryAttempts ?? retryAttempts) != null
                                                            ? `${session.retryCount ?? 0}/${session.maxRetryAttempts ?? retryAttempts}`
                                                            : <span className="text-gray-400">Not set</span>
                                                    }
                                                </td>
                                                <td className="pr-6 pl-4 py-3 text-right">
                                                    <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full border", chipVariantStyles[config.variant])}>
                                                        <span className={cn("w-1.5 h-1.5 rounded-full", chipDotColor[config.variant])} />
                                                        {config.label}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                            <Phone className="w-8 h-8 mb-2" />
                            <p className="text-sm">No call results yet.</p>
                            <p className="text-xs mt-1">Results will appear here once calls are made.</p>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function StatCard({
    icon: Icon,
    label,
    value,
    valueColor,
    subtitle,
}: {
    icon: typeof Phone;
    label: string;
    value: string;
    valueColor?: string;
    subtitle?: string;
}) {
    return (
        <div className="rounded-lg border bg-white dark:bg-gray-900 shadow-sm p-5">
            <div className="flex items-start gap-3">
                <div className="p-2 rounded-full bg-blue-50 dark:bg-blue-900/30">
                    <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</p>
                    <p className={cn("text-2xl font-bold text-gray-900 dark:text-white mt-1", valueColor)}>{value}</p>
                    {subtitle && <p className={cn("text-xs font-medium mt-0.5", valueColor || "text-gray-500")}>{subtitle}</p>}
                </div>
            </div>
        </div>
    );
}
